import React, { useState } from 'react'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import { ExclamationTriangleIcon, MinusIcon } from '@heroicons/react/24/outline'
import { Button as DesignSystemButton } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { WherebyPermissionsModal } from './WherebyPermissionsModal'

// The pieces of the floating call card that are not the call itself.

/** Card title + minimize button. Doubles as the drag handle (`.whereby-drag-handle`). */
export const WherebyFloatCardHeader: React.FC<{ onMinimize: () => void }> = ({ onMinimize }) => {
  const { t: translate } = useTranslation()

  return (
    <Box
      className="whereby-drag-handle"
      sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'grab', userSelect: 'none' }}
    >
      <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '16px' }}>
        {translate('gamer.groupCall.title')}
      </Typography>
      <IconButton
        onClick={onMinimize}
        size="small"
        sx={{
          width: 32,
          height: 32,
          backgroundColor: INTERAX_COLORS.sun,
          color: INTERAX_COLORS.offBlack,
          borderRadius: '999px',
          '&:hover': { backgroundColor: INTERAX_COLORS.sun40 }
        }}
      >
        <MinusIcon style={{ width: 16, height: 16 }} />
      </IconButton>
    </Box>
  )
}

/** Waiting for the room to exist. After a while it offers a retry / leave, in case the create got stuck. */
export const WherebyFloatPreparingRoom: React.FC<{
  isTakingTooLong: boolean
  onRetry: () => void
  onLeaveCall: () => void
}> = ({ isTakingTooLong, onRetry, onLeaveCall }) => {
  const { t: translate } = useTranslation()

  return (
    <Box
      sx={{
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2,
        color: INTERAX_COLORS.white,
        fontSize: '14px'
      }}
    >
      <CircularProgress size={32} sx={{ color: INTERAX_COLORS.sun }} aria-hidden />
      <Box sx={{ opacity: 0.6 }}>{translate('whereby.global.preparingRoom')}</Box>
      {isTakingTooLong ? (
        <>
          <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey, textAlign: 'center', px: 1 }}>
            {translate('whereby.global.preparingTooLong')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <DesignSystemButton variant="primary" onClick={onRetry}>
              {translate('whereby.global.retry')}
            </DesignSystemButton>
            <DesignSystemButton variant="secondary" onClick={onLeaveCall}>
              {translate('whereby.global.leaveCall')}
            </DesignSystemButton>
          </Box>
        </>
      ) : null}
    </Box>
  )
}

// Blocks signaling until camera and microphone access can produce a usable call.
export const WherebyMediaBlockedPanel: React.FC<{ onLeaveCall: () => void }> = ({ onLeaveCall }) => {
  const { t: translate } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        flex: 1,
        minHeight: 220,
        px: { xs: 2, sm: 3, md: 3 },
        py: { xs: 2, sm: 3, md: 3 },
        textAlign: 'center'
      }}
    >
      <ExclamationTriangleIcon style={{ width: 32, height: 32, color: INTERAX_COLORS.redError }} />
      <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '15px' }}>
        {translate('whereby.permissions.blockedTitle', 'Your camera and microphone are blocked')}
      </Typography>
      <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '13px', lineHeight: 1.5 }}>
        {translate(
          'whereby.permissions.blockedBody',
          'The call cannot start until access is allowed — your team would not see or hear you.'
        )}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
        <DesignSystemButton variant="primary" onClick={() => setIsModalOpen(true)}>
          {translate('whereby.permissions.howToFix', 'Learn how to enable them')}
        </DesignSystemButton>
        <DesignSystemButton variant="secondary" onClick={onLeaveCall}>
          {translate('whereby.global.leaveCall')}
        </DesignSystemButton>
      </Box>
      <WherebyPermissionsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Box>
  )
}
